import { useMutation, useIsMutating } from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';

import { updateItem } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Item, ItemUpdatePayload } from 'apis/restaurant-api/merchant-admin/menu2/types';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { cleanFileName } from 'modules/menu/hooks/useProcessMenuImage';
import { updateOffer, uploadSelectionImage } from 'modules/shared-menu/api';
import { Offer } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';

import { buildItemUpdatePayload } from './utils';

type Props = {
  existingImageUrls: string[];
  item: Item | Offer;
  menuId: string;
  type: 'shared-menu' | 'venue-menu';
};

export type UpdatePayload = {
  item: Item | Offer;
  imageUrls: string[];
};

export const isSharedMenuItem = (item: Item | Offer, type: Props['type']): item is Offer =>
  'scope' in item && type === 'shared-menu';

export const useImageUpload = ({ existingImageUrls, item, menuId, type }: Props) => {
  const { mutateAsync: handleUpdateItem } = useMutation(
    ['header-image-upload-update-item', item.id],
    async (payload: UpdatePayload) => {
      const { item, imageUrls } = payload;
      if (isSharedMenuItem(item, type)) {
        const { id, scope, version, ...updatePayload } = {
          ...item,
          image: imageUrls.length > 0 ? imageUrls[0] : undefined,
          images: imageUrls.length > 0 ? imageUrls.map(url => ({ url })) : undefined,
        };
        const updatedOffer = await updateOffer(menuId, item.id, updatePayload);
        return updatedOffer;
      } else {
        const updatePayload: ItemUpdatePayload = buildItemUpdatePayload(item, imageUrls);
        const updatedItem = await updateItem(menuId, item.id, updatePayload);
        return updatedItem;
      }
    },
    {
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const { mutateAsync: handleUploadImages } = useMutation(
    ['header-image-upload', item.id],
    async (validFiles: File[]) => {
      if (validFiles.length === 0) {
        return item;
      }

      const imageFiles = validFiles.map(file => new File([file], cleanFileName(file.name)));
      const newImageUrls = await Promise.all(imageFiles.map(async file => uploadSelectionImage(file)));
      const imageUrls = [...existingImageUrls, ...newImageUrls];
      const updatedItem = await handleUpdateItem({ item, imageUrls });
      return updatedItem;
    },
    {
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const isUploadingImages = !!useIsMutating({ mutationKey: ['header-image-upload', item.id] });
  const isUpdatingItem = !!useIsMutating({ mutationKey: ['header-image-upload-update-item', item.id] });

  return { uploadImages: handleUploadImages, updateItemOrOffer: handleUpdateItem, isUploadingImages, isUpdatingItem };
};
